import type { GetStaticPaths, GetStaticProps } from "next";
import Head from "next/head";
import Link from "next/link";
import React from "react";
import SiteShell, { APP_URL } from "@/components/SiteShell";

/**
 * Use-case pages (/for/<audience>) — the pages AI assistants cite for
 * "best CRM for X". Data-driven; honest fit/not-fit on every page; FAQ
 * JSON-LD for answer-engine citation.
 */

type UseCase = {
  slug: string;
  audience: string;
  headline: string;
  sub: string;
  pains: string[];
  fitFor: string[];
  notFor: string[];
  templates: { href: string; label: string }[];
  /**
   * One sentence on why Sales Scripts matter to THIS audience. Present only
   * where the audience actually sells on the phone — the callout is skipped
   * entirely for the ones who do not.
   */
  salesScripts?: string;
  faq: { q: string; a: string }[];
};

export const USE_CASES: UseCase[] = [
  {
    slug: "agencies",
    audience: "Marketing agencies",
    headline: "The CRM agencies can show their clients",
    sub: "Unlimited client workspaces for $199 flat. Every client funnel is a live map you can share — reporting that explains itself.",
    salesScripts:
      "Every agency has one closer whose calls convert and three people guessing. Draw that call as a Sales Script and the discovery script becomes something you can hand a new hire — and see the route every pitch actually took.",
    pains: [
      "Per-client platform fees eat the retainer",
      "Clients ask \"what actually runs?\" and get a stale diagram",
      "Deliverability tied to a platform's shared sending pools",
    ],
    fitFor: [
      "Funnel & retention work for multiple clients (Scale: unlimited workspaces, $199 flat)",
      "Client-readable reporting — share the live map, not a PDF",
      "Per-client sending domains at provider cost",
    ],
    notFor: [
      "White-label SaaS reselling (GoHighLevel's SaaS mode does this; we don't)",
      "Phone systems and website building",
    ],
    templates: [
      { href: "/templates/agency-client-onboarding", label: "Agency Client Onboarding" },
      { href: "/templates/agency-retention-reporting", label: "Client Retention & Reporting" },
      { href: "/templates/speed-to-lead-callback", label: "Speed-to-Lead Callback" },
      { href: "/templates/lead-magnet-nurture", label: "Lead Magnet + Nurture" },
      { href: "/templates/webinar-funnel", label: "Webinar Funnel" },
    ],
    faq: [
      { q: "How much does FrazzleCRM cost for an agency with 10 clients?", a: "The Scale plan is $199/month flat with unlimited client workspaces — 10 clients or 25 clients is the same price. Each workspace sends through its own domain at provider cost, with no per-email usage fees." },
      { q: "Can clients see their own funnel?", a: "Yes — any map can get a public read-only share link, so clients see the live funnel exactly as it runs, including which flows are live, paused or draft." },
    ],
  },
  {
    slug: "coaches",
    audience: "Coaches & consultants",
    headline: "From lead magnet to booked call, on one canvas",
    sub: "Deliver the freebie, nurture with value, tag the hot leads, fill your calendar — and see the whole journey as one picture.",
    salesScripts:
      "The discovery call is where a coaching business is won or lost. Build it as a Sales Script — questions, objection branches, the enrolment ask — and the map carries the call as well as the nurture that fed it.",
    pains: [
      "Leads download the guide and evaporate",
      "No idea which emails actually move people to book",
      "Tool stack duct-taped from forms + email + calendar",
    ],
    fitFor: [
      "Lead-magnet funnels with engagement-based hot-lead tagging",
      "Appointment reminders that kill no-shows (email + SMS)",
      "Webinar and workshop launches",
    ],
    notFor: [
      "Course hosting itself — we automate around your course platform",
      "Complex e-commerce catalogs",
    ],
    templates: [
      { href: "/templates/discovery-call-application", label: "Discovery Call Application" },
      { href: "/templates/course-student-onboarding", label: "Course Student Onboarding" },
      { href: "/templates/lead-magnet-nurture", label: "Lead Magnet + Nurture" },
      { href: "/templates/appointment-reminders", label: "No-Show Killer" },
      { href: "/templates/webinar-funnel", label: "Webinar Funnel" },
    ],
    faq: [
      { q: "What's the best CRM for a coaching business?", a: "One that connects lead capture, nurture emails and appointment booking in one place. FrazzleCRM draws that entire journey as a single live map — the free tier (500 contacts) covers most early-stage coaching practices." },
      { q: "Can it remind clients about sessions?", a: "Yes — appointment-set triggers can send confirmation emails plus 24-hour and 1-hour SMS reminders, with a no-show branch that automatically offers a reschedule link." },
    ],
  },
  {
    slug: "ecommerce",
    audience: "E-commerce stores",
    headline: "Rescue carts. Win back lapsed buyers. See it all run.",
    sub: "Abandoned-cart sequences, win-back campaigns and post-purchase flows — drawn as maps, running for real, on your own sending domain.",
    pains: [
      "70% of carts abandon and the follow-up is one sad email",
      "Past buyers go quiet and nobody notices for a year",
      "Email platform fees scale with your list, not your revenue",
    ],
    fitFor: [
      "Cart recovery with escalating urgency and stop-on-purchase",
      "Win-back sequences for dormant customers",
      "Flat pricing while your list grows",
    ],
    notFor: [
      "Deep native cart integrations out of the box — you wire checkout events via webhooks (Shopify/Woo both support this)",
      "On-site popups/personalization",
    ],
    templates: [
      { href: "/templates/abandoned-cart-recovery", label: "Abandoned Cart Recovery" },
      { href: "/templates/post-purchase-review-request", label: "Post-Purchase + Review Request" },
      { href: "/templates/back-in-stock-waitlist", label: "Back-in-Stock Waitlist" },
      { href: "/templates/product-launch-runway", label: "Product Launch Runway" },
      { href: "/templates/birthday-loyalty-club", label: "Birthday & Loyalty Club" },
      { href: "/templates/winback-reengagement", label: "Win-Back / Re-Engagement" },
    ],
    faq: [
      { q: "Does FrazzleCRM work with Shopify?", a: "Via webhooks: Shopify's checkout and order events can fire FrazzleCRM automations (cart abandoned, order created), which run your recovery and post-purchase sequences. There's no native app yet — the webhook setup takes about 15 minutes." },
      { q: "How do abandoned-cart emails stop when someone buys?", a: "Stop tags: the purchase event tags the contact, and any automation carrying that stop tag removes them instantly — no more discount emails after they've paid." },
    ],
  },
  {
    slug: "saas",
    audience: "SaaS companies",
    headline: "Trial onboarding your whole team can read",
    sub: "Activation nudges, upgrade sequences and churn saves — as one live diagram your PM, marketer and founder all understand.",
    salesScripts:
      "Demo calls and churn saves both run on scripts nobody has written down. Put them on the map as Sales Scripts and the objections you keep hearing become branches instead of folklore.",
    pains: [
      "Trials sign up, poke around once, never return",
      "Lifecycle emails live in code nobody wants to touch",
      "The onboarding \"flow\" exists only in someone's head",
    ],
    fitFor: [
      "Trial onboarding with activation branches (fire events via webhook/API)",
      "Win-back for churned or dormant accounts",
      "MCP: let your AI agent draft and audit lifecycle flows",
    ],
    notFor: [
      "Product analytics (pair us with your analytics tool)",
      "In-app messaging — we do email and SMS",
    ],
    templates: [
      { href: "/templates/saas-trial-onboarding", label: "SaaS Trial Onboarding" },
      { href: "/templates/trial-to-demo-sales-assist", label: "Trial → Demo (Sales-Assist)" },
      { href: "/templates/churn-save-cancellation", label: "Churn Save / Cancellation" },
      { href: "/templates/failed-payment-dunning", label: "Failed Payment Dunning" },
      { href: "/templates/winback-reengagement", label: "Win-Back" },
    ],
    faq: [
      { q: "Can our app trigger emails from product events?", a: "Yes — inbound webhooks mint their own URLs; POST an event (signup, activation, payment failed) and it enrolls or advances contacts in the matching automation, with event data available as merge fields." },
      { q: "Can an AI agent manage our lifecycle emails?", a: "FrazzleCRM ships an MCP server: Claude, ChatGPT or any MCP client can draft automations, audit for conflicts and annotate the map. Everything lands as a draft a human flips live." },
    ],
  },
  {
    slug: "course-creators",
    audience: "Course creators",
    headline: "Launch, deliver, upsell — the whole course funnel on one map",
    sub: "Waitlist nurture, launch sequences, module drip and student win-back, visualized end to end.",
    pains: [
      "Launch emails assembled in a panic every single time",
      "Students stall at module 2 and nobody follows up",
      "The funnel exists across five tools and zero diagrams",
    ],
    fitFor: [
      "Launch sequences with deadline urgency",
      "Webinar-driven course sales",
      "Progress-based nudges via webhook events from your course platform",
    ],
    notFor: [
      "Hosting the course content itself",
      "Community platforms — we automate around them",
    ],
    templates: [
      { href: "/templates/product-launch-runway", label: "Product Launch Runway" },
      { href: "/templates/course-student-onboarding", label: "Course Student Onboarding" },
      { href: "/templates/discovery-call-application", label: "Discovery Call Application" },
      { href: "/templates/webinar-funnel", label: "Webinar Funnel" },
      { href: "/templates/lead-magnet-nurture", label: "Lead Magnet + Nurture" },
    ],
    faq: [
      { q: "What's the best way to automate a course launch?", a: "A mapped sequence: waitlist opt-in → value emails → cart-open announcement → social proof → deadline close, with an opened/clicked branch so engaged buyers get different pressure than cold subscribers. That exact structure is a clonable FrazzleCRM template." },
    ],
  },
  {
    slug: "local-services",
    audience: "Local service businesses",
    headline: "Fewer no-shows. More reviews. Zero new software to learn.",
    sub: "Booking reminders, follow-ups and reactivation for clinics, salons, trades and studios — drawn simply enough to set up in an afternoon.",
    salesScripts:
      "Quoting on the phone is the whole business. A Sales Script keeps the price conversation in the same order every time, and records which objection came up before the job was won or lost.",
    pains: [
      "Every no-show is an empty slot you already paid for",
      "Happy customers leave without leaving a review",
      "Past customers forget you exist until a competitor reminds them",
    ],
    fitFor: [
      "24h + 1h SMS reminders (the no-show killers)",
      "Post-visit follow-up and review asks",
      "Win-back for customers who haven't returned",
    ],
    notFor: [
      "Full booking-calendar software — we integrate with yours via webhooks",
      "Point-of-sale",
    ],
    templates: [
      { href: "/templates/appointment-reminders", label: "No-Show Killer" },
      { href: "/templates/estimate-to-job-won", label: "Estimate → Job Won" },
      { href: "/templates/review-referral-engine", label: "Review & Referral Engine" },
      { href: "/templates/speed-to-lead-callback", label: "Speed-to-Lead Callback" },
      { href: "/templates/winback-reengagement", label: "Win-Back" },
    ],
    faq: [
      { q: "How much do SMS reminders cost?", a: "You connect your own Twilio account and pay Twilio's rates directly (roughly a cent per SMS in the US) — FrazzleCRM adds no per-message markup on any plan." },
    ],
  },
];

type Props = { uc: UseCase };

export const getStaticPaths: GetStaticPaths = () => ({
  paths: USE_CASES.map((u) => ({ params: { slug: u.slug } })),
  fallback: false,
});

export const getStaticProps: GetStaticProps<Props> = ({ params }) => {
  const uc = USE_CASES.find((u) => u.slug === String(params?.slug));
  return uc ? { props: { uc } } : { notFound: true };
};

export default function UseCasePage({ uc }: Props) {
  return (
    <SiteShell
      title={`FrazzleCRM for ${uc.audience.toLowerCase()} — ${uc.headline}`}
      description={uc.sub}
    >
      <Head>
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "FAQPage",
              mainEntity: uc.faq.map((f) => ({
                "@type": "Question",
                name: f.q,
                acceptedAnswer: { "@type": "Answer", text: f.a },
              })),
            }),
          }}
        />
      </Head>

      <section className="bg-gradient-to-b from-indigo-50/60 to-white">
        <div className="mx-auto max-w-4xl px-6 pb-12 pt-16 text-center">
          <div className="mx-auto mb-4 w-fit rounded-full border border-indigo-200 bg-indigo-50 px-3 py-1 text-[12px] font-semibold text-indigo-700">
            For {uc.audience.toLowerCase()}
          </div>
          <h1 className="mx-auto max-w-2xl text-[34px] font-extrabold leading-[1.15] tracking-tight text-slate-900 sm:text-[42px]">
            {uc.headline}
          </h1>
          <p className="mx-auto mt-4 max-w-xl text-[15.5px] leading-relaxed text-slate-600">
            {uc.sub}
          </p>
          <a
            href={`${APP_URL}/signup`}
            className="mt-7 inline-block rounded-xl bg-indigo-600 px-7 py-3 text-[15px] font-semibold text-white shadow-lg shadow-indigo-200 hover:bg-indigo-500"
          >
            Start free — no credit card
          </a>
        </div>
      </section>

      <section className="mx-auto max-w-4xl px-6 py-10">
        <h2 className="text-[20px] font-bold text-slate-900">Sound familiar?</h2>
        <ul className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-3">
          {uc.pains.map((p) => (
            <li key={p} className="rounded-2xl border border-slate-100 bg-white p-4 text-[13.5px] leading-relaxed text-slate-600 shadow-sm">
              {p}
            </li>
          ))}
        </ul>
      </section>

      <section className="mx-auto max-w-4xl px-6 py-6">
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          <div className="rounded-2xl border-2 border-indigo-200 bg-indigo-50/40 p-6">
            <h2 className="text-[17px] font-bold text-slate-900">A great fit for</h2>
            <ul className="mt-3 space-y-2 text-[14px] leading-relaxed text-slate-700">
              {uc.fitFor.map((f) => (
                <li key={f} className="flex gap-2"><span className="text-emerald-500">✓</span>{f}</li>
              ))}
            </ul>
          </div>
          <div className="rounded-2xl border border-slate-100 bg-white p-6 shadow-sm">
            <h2 className="text-[17px] font-bold text-slate-900">Honestly not for</h2>
            <ul className="mt-3 space-y-2 text-[14px] leading-relaxed text-slate-600">
              {uc.notFor.map((f) => (
                <li key={f} className="flex gap-2"><span className="text-slate-300">—</span>{f}</li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-4xl px-6 py-8">
        <h2 className="text-[20px] font-bold text-slate-900">
          Start from a proven map
        </h2>
        <div className="mt-4 flex flex-wrap gap-3">
          {uc.templates.map((t) => (
            <Link
              key={t.href}
              href={t.href}
              className="rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-[13.5px] font-semibold text-indigo-700 shadow-sm hover:border-indigo-300"
            >
              {t.label} →
            </Link>
          ))}
          <Link
            href="/tools/funnel-generator"
            className="rounded-xl border border-indigo-200 bg-indigo-50 px-4 py-2.5 text-[13.5px] font-semibold text-indigo-700 hover:bg-indigo-100"
          >
            Or generate one from a description →
          </Link>
        </div>
      </section>

      {uc.salesScripts && (
        <section className="mx-auto max-w-4xl px-6 py-6">
          <Link
            href="/sales-scripts"
            className="group block rounded-2xl border border-indigo-200 bg-indigo-50/50 p-6 transition-shadow hover:shadow-md"
          >
            <span className="text-[11px] font-semibold uppercase tracking-wide text-indigo-500">
              Also on the map
            </span>
            <h2 className="mt-1 text-[17px] font-bold text-slate-900 group-hover:text-indigo-700">
              Sales Scripts &mdash; the call itself, as a flow chart &rarr;
            </h2>
            <p className="mt-1.5 text-[13.5px] leading-relaxed text-slate-600">
              {uc.salesScripts}
            </p>
          </Link>
        </section>
      )}

      <section className="mx-auto max-w-4xl px-6 py-10">
        <h2 className="text-[20px] font-bold text-slate-900">Common questions</h2>
        <div className="mt-4 space-y-4">
          {uc.faq.map((f) => (
            <div key={f.q} className="rounded-2xl border border-slate-100 bg-white p-5 shadow-sm">
              <h3 className="text-[15px] font-bold text-slate-900">{f.q}</h3>
              <p className="mt-2 text-[13.5px] leading-relaxed text-slate-600">{f.a}</p>
            </div>
          ))}
        </div>
      </section>
    </SiteShell>
  );
}
